import { useMemo, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useSearchParams } from "react-router-dom";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Sheet, SheetContent, SheetTrigger } from "../../components/ui/sheet";
import { Separator } from "../../components/ui/separator";
import { VERTICALS } from "../../data/verticalConfigs";
import { BRAND } from "../../lib/config";
import { BrandMark } from "../../components/BrandLogo";
import {
  LayoutDashboard, KanbanSquare, CalendarDays, Megaphone, BarChart3, Settings,
  ClipboardCheck, Menu, ArrowLeft, Sparkles
} from "lucide-react";

const NAV = [
  { to: "/demo", end: true, label: "Overview", icon: LayoutDashboard, testid: "nav-overview" },
  { to: "/demo/pipeline", label: "Pipeline", icon: KanbanSquare, testid: "nav-pipeline" },
  { to: "/demo/slots", label: "Slot Queue", icon: ClipboardCheck, testid: "nav-slots" },
  { to: "/demo/calendar", label: "Calendar", icon: CalendarDays, testid: "nav-calendar" },
  { to: "/demo/campaigns", label: "Campaigns", icon: Megaphone, testid: "nav-campaigns" },
  { to: "/demo/reports", label: "Reports", icon: BarChart3, testid: "nav-reports" },
  { to: "/demo/settings", label: "Settings", icon: Settings, testid: "nav-settings" },
];

const PAGE_TITLES = {
  "/demo": "Overview",
  "/demo/pipeline": "Pipeline Board",
  "/demo/slots": "Slot Coordination Queue",
  "/demo/calendar": "Bookings & Calendar",
  "/demo/campaigns": "Campaign Templates",
  "/demo/reports": "Reports",
  "/demo/settings": "Settings",
};

function SidebarContent({ onNavigate }) {
  return (
    <div className="flex flex-col h-full">
      <div className="px-4 py-5">
        <Link to="/" className="flex items-center gap-2 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)] focus-visible:ring-offset-2" onClick={onNavigate} aria-label="EnquiryOps home">
          <BrandMark size={32} />
          <div>
            <p className="font-heading font-semibold text-sm text-foreground leading-none">{BRAND.name}</p>
            <p className="text-[10px] text-muted-foreground mt-0.5">Enquiry Conversion OS</p>
          </div>
        </Link>
      </div>
      <Separator />
      <nav className="flex-1 px-2.5 py-4 space-y-1" data-testid="dashboard-sidebar-nav">
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={onNavigate}
            data-testid={item.testid}
            className={({ isActive }) =>
              `flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                isActive ? "bg-[hsl(215_84%_22%)] text-white" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
              }`
            }
          >
            <item.icon className="h-4 w-4" /> {item.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3">
        <div className="bg-secondary rounded-xl p-3.5">
          <div className="flex items-center gap-1.5 mb-1">
            <Sparkles className="h-3.5 w-3.5 text-accent" />
            <p className="text-xs font-semibold text-foreground">Demo environment</p>
          </div>
          <p className="text-[11px] text-muted-foreground leading-snug">All data is realistic mock data. Your centre's live pipeline would appear here.</p>
          <Button size="sm" className="w-full rounded-lg mt-2.5 h-8 text-xs" asChild data-testid="sidebar-reserve-button">
            <Link to="/reserve">Reserve Founding Slot</Link>
          </Button>
        </div>
        <Link to="/" className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors mt-3 px-1" data-testid="sidebar-back-to-site">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to site
        </Link>
      </div>
    </div>
  );
}

export default function DashboardLayout() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const initialVertical = ["enrichment", "aesthetic", "dental"].includes(searchParams.get("vertical")) ? searchParams.get("vertical") : "enrichment";
  const [vertical] = useState(initialVertical);
  const [branchFilter, setBranchFilter] = useState("all");
  const [mobileOpen, setMobileOpen] = useState(false);

  const config = VERTICALS[vertical];
  const pageTitle = useMemo(() => {
    if (location.pathname.startsWith("/demo/leads/")) return `${config.entityLabel} Detail`;
    return PAGE_TITLES[location.pathname] || "Overview";
  }, [location.pathname, config.entityLabel]);

  return (
    <div className="min-h-screen bg-[hsl(210_30%_96%)] flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-60 shrink-0 bg-white border-r border-border flex-col fixed inset-y-0">
        <SidebarContent />
      </aside>

      <div className="flex-1 lg:ml-60 flex flex-col min-w-0">
        {/* Topbar */}
        <header className="sticky top-0 z-40 bg-white border-b border-border">
          <div className="px-4 sm:px-6 h-16 flex items-center gap-3">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="secondary" size="icon" className="md:hidden rounded-xl border border-border shrink-0" data-testid="dashboard-sidebar-open-button">
                  <Menu className="h-4 w-4" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-64 bg-white">
                <SidebarContent onNavigate={() => setMobileOpen(false)} />
              </SheetContent>
            </Sheet>

            <div className="min-w-0">
              <h1 className="font-heading font-semibold text-base sm:text-lg text-foreground truncate" data-testid="dashboard-page-title">{pageTitle}</h1>
              <p className="text-[11px] text-muted-foreground truncate hidden sm:block">{config.headline} · {config.subheadline}</p>
            </div>

            <div className="ml-auto flex items-center gap-2">
              <Badge className="bg-secondary text-foreground border border-border hover:bg-secondary hidden sm:inline-flex" data-testid="demo-data-badge">Demo data</Badge>
              <Select value={branchFilter} onValueChange={setBranchFilter}>
                <SelectTrigger className="h-9 rounded-xl w-[120px] sm:w-[150px] text-xs bg-white hidden md:flex" data-testid="dashboard-branch-filter">
                  <SelectValue placeholder={`All ${config.branchLabelPlural}`} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All {config.branchLabelPlural}</SelectItem>
                  {config.branches.map((b) => (
                    <SelectItem key={b} value={b}>{b}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </header>

        <main className="flex-1 p-4 sm:p-6">
          <Outlet context={{ vertical, config, branchFilter }} />
        </main>
      </div>
    </div>
  );
}
